<template>
  <n-layout class="layout">
    <!-- Header with Navigation -->
    <n-layout-header class="header">
      <div class="header-content">
        <div class="logo">
          <h1>Incredible</h1>
        </div>
        
        <!-- Navigation Tabs -->
        <n-tabs 
          v-model:value="activeTab" 
          type="line" 
          class="nav-tabs"
          @update:value="handleTabChange"
        >
          <n-tab-pane name="chat" tab="Chat" />
          <n-tab-pane name="gallery" tab="Gallery" />
          <n-tab-pane name="brand" tab="Brand" />
          <n-tab-pane name="workspace" tab="Workspace" />
          <n-tab-pane name="research" tab="Research" />
          <n-tab-pane name="admin" tab="Admin" />
          <n-tab-pane name="settings" tab="Settings" />
        </n-tabs>

        <!-- Company and Project -->
        <div class="brand-project-selectors" v-if="user">
          <n-tag type="info" size="small" :bordered="false" style="cursor: pointer;" @click="router.push('/brand-selection')">
            {{ currentBrandCode || 'No Company' }}
          </n-tag>
          <n-select
            v-model:value="selectedProjectId"
            :options="projectOptions"
            placeholder="Select Project"
            clearable
            :disabled="!selectedBrandId"
            style="width: 150px;"
            @update:value="handleProjectChange"
          />
        </div>

        <!-- User Actions -->
        <div class="user-actions">
          <n-button 
            v-if="!user" 
            @click="$router.push('/login')"
            type="primary"
            ghost
            size="small"
          >
            Login
          </n-button>
          <n-dropdown v-else :options="userMenuOptions" @select="handleUserAction">
            <n-button quaternary circle>
              <n-avatar round size="small">
                {{ user?.name?.charAt(0).toUpperCase() || 'U' }}
              </n-avatar>
            </n-button>
          </n-dropdown>
        </div>
      </div>
    </n-layout-header>

    <!-- Main Content -->
    <n-layout-content class="content">
      <div class="content-wrapper">
        <!-- Tab Content -->
        <component :is="currentComponent" />
      </div>
    </n-layout-content>
  </n-layout>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { useMessage } from 'naive-ui'
import { apiClient } from '@/services/api'
import { useRouter } from 'vue-router'
import GalleryTab from './tabs/GalleryTab.vue'
import WorkspaceTab from './tabs/WorkspaceTab.vue'
import BrandTab from './tabs/BrandTab.vue'
import ChatTab from './tabs/ChatTab.vue'
import AdminTab from './tabs/AdminTab.vue'
import SettingsTab from './tabs/SettingsTab.vue'
import ResearchTab from './tabs/ResearchTab.vue'

// Reactive state
const activeTab = ref('brand')
interface CurrentUser { id: string; email: string; name: string | null; role: string; createdAt: string }
const user = ref<CurrentUser | null>(null)

// Brand and Project state
interface Brand {
  id: string;
  name: string;
  code: string;
  description?: string;
  contactName?: string;
  contactEmail?: string;
  phone?: string;
  address?: string;
  website?: string;
  igHandle?: string;
  fbHandle?: string;
  logoUrls?: string; // JSON string
  colors?: string; // JSON string
  typefaces?: string; // JSON string
  usp?: string;
  notes?: string;
  voiceTone?: string;
  taglines?: string; // JSON string
  elevatorPitch?: string;
  teamMembers?: string; // JSON string
  createdAt?: string;
  updatedAt?: string;
}
interface Project { id: string; name: string; code: string; brandId: string; description?: string }
const brands = ref<Brand[]>([])
const projects = ref<Project[]>([])
const selectedBrandId = ref<string | null>(null)
const selectedProjectId = ref<string | null>(null)

const message = useMessage()
const router = useRouter()

// Tab components mapping
const tabComponents = {
  gallery: GalleryTab,
  workspace: WorkspaceTab,
  brand: BrandTab,
  chat: ChatTab,
  admin: AdminTab,
  settings: SettingsTab,
  research: ResearchTab,
}

const currentComponent = computed(() => tabComponents[activeTab.value as keyof typeof tabComponents])

const currentBrandCode = computed(() => {
  if (!selectedBrandId.value) {
    return null
  }
  return brands.value.find((brand) => brand.id === selectedBrandId.value)?.code ?? null
})

const projectOptions = computed(() => 
  projects.value
    .filter(p => p.brandId === selectedBrandId.value)
    .map(p => ({ label: p.code, value: p.id }))
)

// Load brands and projects
const loadBrands = async () => {
  try {
    const response = await apiClient.getBrands({ limit: 100 })
    brands.value = response.brands
  } catch (error) {
    console.error('Failed to load brands:', error)
  }
}

const loadProjects = async (brandId?: string) => {
  try {
    const response = await apiClient.getProjects({ brandId, limit: 100 })
    projects.value = response.projects
  } catch (error) {
    console.error('Failed to load projects:', error)
  }
}

const emitBrandProjectChanged = () => {
  window.dispatchEvent(new CustomEvent('brand-project-changed', {
    detail: { brandId: selectedBrandId.value, projectId: selectedProjectId.value }
  }))
}

const handleProjectChange = (projectId: string | null) => {
  selectedProjectId.value = projectId
  // Save to localStorage
  if (projectId && selectedBrandId.value) {
    localStorage.setItem(`selectedProject_${selectedBrandId.value}`, projectId)
  }
  // Emit event for other components
  emitBrandProjectChanged()
}

// User menu options
const userMenuOptions = [
  {
    label: 'Profile',
    key: 'profile',
  },
  {
    label: 'Settings',
    key: 'settings',
  },
  {
    label: 'View Queue',
    key: 'view-queue',
  },
  {
    type: 'divider',
    key: 'divider',
  },
  {
    label: 'Logout',
    key: 'logout',
  },
]

// Methods
const handleTabChange = (tabName: string) => {
  activeTab.value = tabName
  // Update URL without navigation
  window.history.replaceState({}, '', `#${tabName}`)
}

// Listen for tab change events from other components
window.addEventListener('tab-change', ((event: CustomEvent) => {
  if (event.detail?.tab) {
    handleTabChange(event.detail.tab)
  }
}) as EventListener)

// Listen for project creation events to reload projects list
window.addEventListener('project-created', ((event: Event) => {
  const customEvent = event as CustomEvent
  if (customEvent.detail?.brandId) {
    loadProjects(customEvent.detail.brandId).then(() => {
      if (customEvent.detail?.projectId) {
        selectedProjectId.value = customEvent.detail.projectId
        handleProjectChange(customEvent.detail.projectId)
      }
    })
  }
}) as EventListener)

const handleUserAction = async (key: string) => {
  switch (key) {
    case 'profile':
      // TODO: Navigate to profile
      break
    case 'settings':
      activeTab.value = 'settings'
      handleTabChange('settings')
      break
    case 'view-queue':
      window.dispatchEvent(new CustomEvent('open-queue-modal'))
      break
    case 'logout':
      await handleLogout()
      break
  }
}

const handleLogout = async () => {
  try {
    await apiClient.logout()
    user.value = null
    localStorage.removeItem('selectedBrandId')
    message.success('Logged out successfully')
    // Redirect to login page
    router.push('/login')
  } catch (error) {
    message.error('Logout failed')
  }
}

// Initialize user state
const initializeUser = async () => {
  try {
    user.value = await apiClient.getCurrentUser() as CurrentUser
  } catch (error) {
    // User not authenticated, redirect to login
    user.value = null
    if (window.location.pathname !== '/login' && window.location.pathname !== '/register') {
      router.push('/login')
    }
  }
}

watch(user, (newUser) => {
  // If user becomes null and we're not already on login/register, redirect to login
  if (!newUser && router.currentRoute.value.path !== '/login' && router.currentRoute.value.path !== '/register') {
    router.push('/login')
  }
})

// Initialize on mount
onMounted(async () => {
  await initializeUser()
  if (!user.value) {
    return
  }
  
  // Load brands and projects
  await loadBrands()
  
  // Restore selected company/project from localStorage
  const savedBrandId = localStorage.getItem('selectedBrandId')
  const hasSavedBrand = !!savedBrandId && brands.value.some((brand) => brand.id === savedBrandId)

  if (!hasSavedBrand || !savedBrandId) {
    localStorage.removeItem('selectedBrandId')
    await router.push('/brand-selection')
    return
  }

  selectedBrandId.value = savedBrandId
  await loadProjects(savedBrandId)

  const savedProjectId = localStorage.getItem(`selectedProject_${savedBrandId}`)
  if (savedProjectId) {
    const hasSavedProject = projects.value.some((project) => project.id === savedProjectId)
    if (hasSavedProject) {
      selectedProjectId.value = savedProjectId
    } else {
      selectedProjectId.value = null
      localStorage.removeItem(`selectedProject_${savedBrandId}`)
    }
  }

  emitBrandProjectChanged()
  
  // Check for hash in URL to set initial tab
  const hash = window.location.hash.slice(1)
  if (hash && tabComponents[hash as keyof typeof tabComponents]) {
    activeTab.value = hash
  }
})
</script>

<style scoped>
.layout {
  height: 100vh;
  display: flex;
  flex-direction: column;
}

.header {
  border-bottom: 1px solid var(--border-color);
  background: var(--card-color);
}

.header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 64px;
  padding: 0 24px;
  width: 100%;
}

.logo h1 {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--primary-color);
}

.nav-tabs {
  flex: 1;
  margin: 0 48px;
}

.brand-project-selectors {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-right: 24px;
}

.user-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.content {
  flex: 1;
  overflow: hidden;
}

.content-wrapper {
  height: 100%;
  overflow-y: auto;
  padding: 0;
  background: var(--base-color);
}

/* Tab content styling */
:deep(.n-tabs-nav) {
  margin-bottom: 0;
}

:deep(.n-tabs-content) {
  height: 100%;
}

:deep(.n-tab-pane) {
  height: 100%;
}
</style>
